import { Component, OnInit } from '@angular/core';
import { TableLazyLoadEvent, TableModule } from "primeng/table";
import { Router } from "@angular/router";
import { JournalEntriesService } from "./journal-entries.service";
import { SessionStorageService } from "../../services/session-storage.service";
import { JournalEntry } from "./models/journal-entry-header.model";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressBarModule } from "primeng/progressbar";
import { ChipModule } from "primeng/chip";
import { TagModule } from "primeng/tag";
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';


@Component({
    selector: 'app-journal-entries',
    templateUrl: './journal-entries.component.html',
    styleUrls: ['./journal-entries.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TableModule,
        ButtonModule,
        TooltipModule,
        ProgressBarModule,
        ChipModule,
        TagModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ],
    providers: [
        JournalEntriesService,
        SessionStorageService
    ]
})
export class JournalEntriesComponent implements OnInit {

    journalEntries: JournalEntry[] = [];
    first = 0;
    rows = 20;
    totalRequests = 0;
    isLoading = true;

    canAddJournalEntry = false;
    canEditJournalEntry = false;

    constructor(private router: Router,
        private sessionStorageService: SessionStorageService,
        private journalEntriesService: JournalEntriesService,
        private permissionService: PermissionService) {
        this.journalEntries = [];
    }

    addJournalEntry() {
        this.router.navigate(['/accounts/enterprise/journal-entries/add']);
    }

    importJournalEntries() {
        this.router.navigate(['/accounts/enterprise/journal-entries/import']);
    }

    viewJournalEntry(entry: JournalEntry) {
        if (entry.entryId) {
            this.router.navigate(['/accounts/enterprise/journal-entries/view', entry.entryId]);
        }
    }

    editJournalEntry(journalEntry: JournalEntry) {
        this.router.navigate(['/accounts/enterprise/journal-entries/edit', journalEntry.entryId]);
    }


    ngOnInit(): void {
        this.canAddJournalEntry = this.permissionService.hasPermission(app_permissions.accounts.journal.create);
        this.canEditJournalEntry = this.permissionService.hasPermission(app_permissions.accounts.journal.edit);
    }


    loadData(event: TableLazyLoadEvent) {
        this.isLoading = true;
        this.first = event.first ?? 0;
        this.rows = event.rows ?? 20;
        let pageSize = event.rows ?? 20;
        if (pageSize == 0)
            pageSize = 20;
        const page = Math.floor((event.first ?? 0) / pageSize);
        this.journalEntries = [];
        this.journalEntriesService.getAllJournalEntriesByCompany(this.sessionStorageService.getCompany()?.id ?? 0, page, pageSize).subscribe(ledgerListWrapper => {
            this.journalEntries = ledgerListWrapper.items;
            this.journalEntries.forEach(journalEntry => {
                journalEntry.items = journalEntry.items || [];
                journalEntry.items.sort((a, b) => b.debit - a.debit);
            });
            this.totalRequests = ledgerListWrapper.count;
            this.isLoading = false;
        });
    }

    get metrics(): PageMetric[] {
        return [
            { label: 'Loaded Entries', value: this.journalEntries.length, hint: `${this.totalRequests} total`, tone: 'info' },
            { label: 'Posted', value: this.journalEntries.filter(entry => this.normalizedStatus(entry.postingStatus || entry.state) === 'POSTED').length, tone: 'success' },
            { label: 'Open Workflow', value: this.journalEntries.filter(entry => this.normalizedStatus(entry.postingStatus || entry.state) !== 'POSTED').length, tone: 'warn' },
            { label: 'Page Amount', value: this.formatMoney(this.journalEntries.reduce((sum, entry) => sum + Number(entry.amount || 0), 0), this.journalEntries[0]?.currencyCode), tone: 'default' }
        ];
    }

    statusSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const normalized = this.normalizedStatus(status);
        if (normalized === 'POSTED' || normalized === 'APPROVED') return 'success';
        if (normalized === 'DRAFT' || normalized === 'SUBMITTED') return 'info';
        if (normalized === 'ON_HOLD' || normalized === 'PENDING') return 'warn';
        if (['REJECTED', 'CANCELLED', 'VOIDED', 'REVERSED'].includes(normalized)) return 'danger';
        return 'secondary';
    }

    displayStatus(entry: JournalEntry): string {
        return this.normalizedStatus(entry.postingStatus || entry.documentStatus || entry.state || 'DRAFT');
    }

    formatMoney(amount?: number, currencyCode = ''): string {
        return `${currencyCode || ''} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();
    }

    private normalizedStatus(status?: string): string {
        return (status || '').toUpperCase();
    }
}
